import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Loader2,
  AlertCircle,
  Target,
  TrendingUp,
  MapPin,
  Calendar as CalendarIcon,
  Building2,
  Briefcase,
} from "lucide-react";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";

const TradeMissionAnalytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event_slug } = useParams();
  const [eventData, setEventData] = useState(location.state?.eventData || null);
  const [isLoading, setIsLoading] = useState(!location.state?.eventData);
  const [error, setError] = useState(null);

  const [analytics, setAnalytics] = useState({
    participantEngagement: {
      registered: 65,
      confirmed: 35,
      total: 200,
    },
    participationData: [
      { day: "Mon", participants: 24 },
      { day: "Tue", participants: 36 },
      { day: "Wed", participants: 30 },
      { day: "Thurs", participants: 40 },
      { day: "Fri", participants: 36 },
      { day: "Sat", participants: 20 },
      { day: "Sun", participants: 14 },
    ],
    attendeeDemographics: {
      ageGroups: [
        { group: "18-25", count: 48, percentage: 24 },
        { group: "26-35", count: 72, percentage: 36 },
        { group: "36-45", count: 48, percentage: 24 },
        { group: "46-55", count: 24, percentage: 12 },
        { group: "55+", count: 8, percentage: 4 },
      ],
      gender: [
        { type: "Male", count: 112, percentage: 56 },
        { type: "Female", count: 80, percentage: 40 },
        { type: "Other", count: 8, percentage: 4 },
      ],
      location: [
        { city: "Accra", count: 100, percentage: 50 },
        { city: "Kumasi", count: 48, percentage: 24 },
        { city: "Tamale", count: 32, percentage: 16 },
        { city: "Other", count: 20, percentage: 10 },
      ],
    },
    companiesOverview: {
      servicesInterest: [
        { code: "SS", label: "Small Scale", count: 60, percentage: 30 },
        { code: "TMO", label: "Trade Mission Opportunities", count: 50, percentage: 25 },
        { code: "GBA/A", label: "General Business Advisory/Assistance", count: 40, percentage: 20 },
        { code: "N&E", label: "Networking & Events", count: 35, percentage: 17.5 },
        { code: "Others", label: "Others", count: 15, percentage: 7.5 },
      ],
      natureOfCompany: [
        { code: "MG", label: "Manufacturer", count: 45, percentage: 22.5 },
        { code: "WS", label: "Wholesaler", count: 40, percentage: 20 },
        { code: "DT", label: "Distributor", count: 35, percentage: 17.5 },
        { code: "SV", label: "Services", count: 30, percentage: 15 },
        { code: "IP", label: "Importers", count: 25, percentage: 12.5 },
        { code: "EP", label: "Exporters", count: 20, percentage: 10 },
        { code: "Others", label: "Others", count: 5, percentage: 2.5 },
      ],
    },
  });

  useEffect(() => {
    if (eventData) {
      const totalSlots = eventData.total_participant_slots ? parseInt(eventData.total_participant_slots) : 200;
      const registered = Math.round(totalSlots * 0.65); // 65% registered
      const confirmed = Math.round(totalSlots * 0.35); // 35% confirmed
      
      // Generate dummy attendee demographics
      const attendeeDemographics = {
        ageGroups: [
          { group: "18-25", count: Math.round(totalSlots * 0.24), percentage: 24 },
          { group: "26-35", count: Math.round(totalSlots * 0.36), percentage: 36 },
          { group: "36-45", count: Math.round(totalSlots * 0.24), percentage: 24 },
          { group: "46-55", count: Math.round(totalSlots * 0.12), percentage: 12 },
          { group: "55+", count: Math.round(totalSlots * 0.04), percentage: 4 },
        ],
        gender: [
          { type: "Male", count: Math.round(totalSlots * 0.56), percentage: 56 },
          { type: "Female", count: Math.round(totalSlots * 0.40), percentage: 40 },
          { type: "Other", count: Math.round(totalSlots * 0.04), percentage: 4 },
        ],
        location: [
          { city: "Accra", count: Math.round(totalSlots * 0.50), percentage: 50 },
          { city: "Kumasi", count: Math.round(totalSlots * 0.24), percentage: 24 },
          { city: "Tamale", count: Math.round(totalSlots * 0.16), percentage: 16 },
          { city: "Other", count: Math.round(totalSlots * 0.10), percentage: 10 },
        ],
      };

      // Generate dummy companies overview
      const companiesOverview = {
        servicesInterest: [
          { code: "SS", label: "Small Scale", count: Math.round(totalSlots * 0.30), percentage: 30 },
          { code: "TMO", label: "Trade Mission Opportunities", count: Math.round(totalSlots * 0.25), percentage: 25 },
          { code: "GBA/A", label: "General Business Advisory/Assistance", count: Math.round(totalSlots * 0.20), percentage: 20 },
          { code: "N&E", label: "Networking & Events", count: Math.round(totalSlots * 0.175), percentage: 17.5 },
          { code: "Others", label: "Others", count: Math.round(totalSlots * 0.075), percentage: 7.5 },
        ],
        natureOfCompany: [
          { code: "MG", label: "Manufacturer", count: Math.round(totalSlots * 0.225), percentage: 22.5 },
          { code: "WS", label: "Wholesaler", count: Math.round(totalSlots * 0.20), percentage: 20 },
          { code: "DT", label: "Distributor", count: Math.round(totalSlots * 0.175), percentage: 17.5 },
          { code: "SV", label: "Services", count: Math.round(totalSlots * 0.15), percentage: 15 },
          { code: "IP", label: "Importers", count: Math.round(totalSlots * 0.125), percentage: 12.5 },
          { code: "EP", label: "Exporters", count: Math.round(totalSlots * 0.10), percentage: 10 },
          { code: "Others", label: "Others", count: Math.round(totalSlots * 0.025), percentage: 2.5 },
        ],
      };

      setAnalytics({
        participantEngagement: {
          registered: Math.round((registered / totalSlots) * 100),
          confirmed: Math.round((confirmed / totalSlots) * 100),
          total: totalSlots,
        },
        participationData: [
          { day: "Mon", participants: Math.round(totalSlots * 0.12) },
          { day: "Tue", participants: Math.round(totalSlots * 0.18) },
          { day: "Wed", participants: Math.round(totalSlots * 0.15) },
          { day: "Thurs", participants: Math.round(totalSlots * 0.20) },
          { day: "Fri", participants: Math.round(totalSlots * 0.18) },
          { day: "Sat", participants: Math.round(totalSlots * 0.10) },
          { day: "Sun", participants: Math.round(totalSlots * 0.07) },
        ],
        attendeeDemographics: attendeeDemographics,
        companiesOverview: companiesOverview,
      });
    } else {
      // Default dummy data when eventData is not available
      setAnalytics({
        participantEngagement: {
          registered: 65,
          confirmed: 35,
          total: 200,
        },
        participationData: [
          { day: "Mon", participants: 24 },
          { day: "Tue", participants: 36 },
          { day: "Wed", participants: 30 },
          { day: "Thurs", participants: 40 },
          { day: "Fri", participants: 36 },
          { day: "Sat", participants: 20 },
          { day: "Sun", participants: 14 },
        ],
        attendeeDemographics: {
          ageGroups: [
            { group: "18-25", count: 48, percentage: 24 },
            { group: "26-35", count: 72, percentage: 36 },
            { group: "36-45", count: 48, percentage: 24 },
            { group: "46-55", count: 24, percentage: 12 },
            { group: "55+", count: 8, percentage: 4 },
          ],
          gender: [
            { type: "Male", count: 112, percentage: 56 },
            { type: "Female", count: 80, percentage: 40 },
            { type: "Other", count: 8, percentage: 4 },
          ],
          location: [
            { city: "Accra", count: 100, percentage: 50 },
            { city: "Kumasi", count: 48, percentage: 24 },
            { city: "Tamale", count: 32, percentage: 16 },
            { city: "Other", count: 20, percentage: 10 },
          ],
        },
        companiesOverview: {
          servicesInterest: [
            { code: "SS", label: "Small Scale", count: 60, percentage: 30 },
            { code: "TMO", label: "Trade Mission Opportunities", count: 50, percentage: 25 },
            { code: "GBA/A", label: "General Business Advisory/Assistance", count: 40, percentage: 20 },
            { code: "N&E", label: "Networking & Events", count: 35, percentage: 17.5 },
            { code: "Others", label: "Others", count: 15, percentage: 7.5 },
          ],
          natureOfCompany: [
            { code: "MG", label: "Manufacturer", count: 45, percentage: 22.5 },
            { code: "WS", label: "Wholesaler", count: 40, percentage: 20 },
            { code: "DT", label: "Distributor", count: 35, percentage: 17.5 },
            { code: "SV", label: "Services", count: 30, percentage: 15 },
            { code: "IP", label: "Importers", count: 25, percentage: 12.5 },
            { code: "EP", label: "Exporters", count: 20, percentage: 10 },
            { code: "Others", label: "Others", count: 5, percentage: 2.5 },
          ],
        },
      });
    }
  }, [eventData]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (location.state?.eventData) {
        setEventData(location.state.eventData);
        setIsLoading(false);
        return;
      }

      if (!event_slug) {
        setError("Trade Mission identifier is missing");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const eventResponse = await eventService.getEvent(event_slug);

        if (!eventResponse.status || !eventResponse.data) {
          throw new Error(eventResponse.message || "Failed to fetch trade mission");
        }

        let event = null;
        if (
          eventResponse.data.status_code === 200 &&
          eventResponse.data.data
        ) {
          event = eventResponse.data.data;
        } else if (eventResponse.data && !eventResponse.data.status_code) {
          event = eventResponse.data;
        }

        if (!event) {
          throw new Error("Trade Mission data not found");
        }

        // Verify it's a trade mission
        const eventTypes = Array.isArray(event.event_type) ? event.event_type : [event.event_type];
        if (!eventTypes.includes("trade_mission")) {
          throw new Error("This event is not a trade mission");
        }

        setEventData(event);
      } catch (err) {
        const errorMessage =
          err.message || "An error occurred while loading trade mission data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [event_slug, location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [event_slug, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-orange-600 animate-spin" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !eventData) {
    return (
      <div className="px-6 py-4">
        <button
          onClick={() => navigate("/admin/media/conference")}
          className="flex items-center mb-6 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Conferences & Trade Missions</span>
        </button>

        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="p-4 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Error Loading Analytics
          </h2>
          <p className="mb-6 text-gray-600">{error || "Trade Mission not found"}</p>
          <button
            onClick={() => navigate("/admin/media/conference")}
            className="px-6 py-2 text-white bg-orange-600 rounded-lg transition-colors hover:bg-orange-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between w-full">
        <button
          onClick={() => navigate(`/admin/media/trade-mission/view/${event_slug}`, {
            state: { eventData: eventData },
          })}
          className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg transition-colors hover:bg-orange-700 cursor-pointer w-full sm:w-auto self-start"
        >
          <ArrowLeft className="mr-2 w-4 h-4 flex-shrink-0" />
          <span className="truncate">Back to Trade Mission Details</span>
        </button>
        <div className="w-full sm:w-auto">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
            {eventData.event_title || eventData.venue_name || "Trade Mission"} Analytics
          </h1>
        </div>
      </div>

      {/* Participant Analytics */}
      <div>
        <h2 className="mb-4 text-xl font-bold text-gray-900">Participant Analytics</h2>
        {true ? (
          <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Participant Engagement */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="mb-6">
                <h3 className="mb-1 text-lg font-semibold text-gray-900">
                  Participant Engagement
                </h3>
                <p className="text-xs text-gray-500">Registration and confirmation status</p>
              </div>
              <div className="flex flex-col items-center space-y-6 lg:flex-row lg:items-start lg:space-y-0 lg:space-x-8">
                <div className="relative flex-shrink-0" style={{ width: '180px', height: '180px' }}>
                  <svg width="180" height="180" className="transform -rotate-90">
                    <circle
                      cx="90"
                      cy="90"
                      r="70"
                      fill="transparent"
                      stroke="#F3F4F6"
                      strokeWidth="16"
                    />
                    <circle
                      cx="90"
                      cy="90"
                      r="70"
                      fill="transparent"
                      stroke="#10B981"
                      strokeWidth="16"
                      strokeDasharray={`${(analytics.participantEngagement.registered / 100) * 439.82} 439.82`}
                      strokeLinecap="round"
                      className="transition-all duration-500"
                    />
                    <circle
                      cx="90"
                      cy="90"
                      r="70"
                      fill="transparent"
                      stroke="#FED7AA"
                      strokeWidth="16"
                      strokeDasharray={`${(analytics.participantEngagement.confirmed / 100) * 439.82} 439.82`}
                      strokeDashoffset={`-${(analytics.participantEngagement.registered / 100) * 439.82}`}
                      strokeLinecap="round"
                      className="transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-xs font-medium text-gray-500 mb-1">Total Slots</p>
                    <p className="text-3xl font-bold text-gray-900">{analytics.participantEngagement.total.toLocaleString()}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {analytics.participantEngagement.registered}% Registered
                    </p>
                  </div>
                </div>
                <div className="flex-1 w-full space-y-4">
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-500 rounded-lg">
                          <Target className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Registered</p>
                          <p className="text-xs text-gray-600">Confirmed participants</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">
                          {analytics.participantEngagement.registered}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {Math.round((analytics.participantEngagement.registered / 100) * analytics.participantEngagement.total).toLocaleString()} people
                        </p>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-green-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${analytics.participantEngagement.registered}%` }}
                      />
                    </div>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-orange-500 rounded-lg">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Pending</p>
                          <p className="text-xs text-gray-600">Awaiting confirmation</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-orange-600">
                          {analytics.participantEngagement.confirmed}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {Math.round((analytics.participantEngagement.confirmed / 100) * analytics.participantEngagement.total).toLocaleString()} people
                        </p>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-orange-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-500"
                        style={{ width: `${analytics.participantEngagement.confirmed}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Participation Tracking */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="mb-1 text-lg font-semibold text-gray-900">
                      Daily Participation Tracking
                    </h3>
                    <p className="text-xs text-gray-500">Weekly participation trends</p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
              </div>
              <ParticipationChart data={analytics.participationData} />
            </div>
          </div>

          {/* Attendee Demographics - Full Width */}
          <div className="mt-6">
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="mb-1 text-lg font-semibold text-gray-900">
                      Participants Demographics
                    </h3>
                    <p className="text-xs text-gray-500">
                      Age, gender, and location breakdown
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </div>
              <AttendeeDemographics demographics={analytics.attendeeDemographics} />
            </div>
          </div>

          {/* Companies Overview - Full Width */}
          <div className="mt-6">
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="mb-1 text-lg font-semibold text-gray-900">
                      Companies Overview
                    </h3>
                    <p className="text-xs text-gray-500">
                      Services interest and nature of company breakdown
                    </p>
                  </div>
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                </div>
              </div>
              <CompaniesOverview overview={analytics.companiesOverview} />
            </div>
          </div>
          </>
        ) : (
          <div className="p-12 bg-white rounded-xl border border-gray-200 shadow-sm text-center">
            <div className="p-4 mb-4 bg-orange-100 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
              <Users className="w-10 h-10 text-orange-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              No Participant Data Available
            </h3>
            <p className="text-sm text-gray-600">
              Participant analytics will appear here once participants start registering for this trade mission.
            </p>
            {eventData.total_participant_slots && (
              <p className="mt-4 text-sm font-medium text-gray-700">
                Total Slots Available: {parseInt(eventData.total_participant_slots).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Participation Chart Component
const ParticipationChart = ({ data }) => {
  const maxParticipants = Math.max(...data.map((d) => d.participants), 1);
  const chartHeight = 180;
  const padding = 15;
  const svgWidth = 100;

  const coordinates = data.map((d, i) => {
    const x = padding + (i * (svgWidth - padding * 2)) / (data.length - 1);
    const y = chartHeight - padding - (d.participants / maxParticipants) * (chartHeight - padding * 2);
    return { x, y };
  });

  const createSmoothPath = (coords) => {
    if (coords.length < 2) return "";
    let path = `M ${coords[0].x}% ${coords[0].y}`;
    for (let i = 0; i < coords.length - 1; i++) {
      const current = coords[i];
      const next = coords[i + 1];
      const prev = coords[i - 1];
      const afterNext = coords[i + 2];
      const cp1x = current.x + (next.x - (prev?.x || current.x)) * 0.3;
      const cp1y = current.y + (next.y - (prev?.y || current.y)) * 0.3;
      const cp2x = next.x - ((afterNext?.x || next.x) - current.x) * 0.3;
      const cp2y = next.y - ((afterNext?.y || next.y) - current.y) * 0.3;
      path += ` C ${cp1x}% ${cp1y}, ${cp2x}% ${cp2y}, ${next.x}% ${next.y}`;
    }
    return path;
  };

  const smoothPath = createSmoothPath(coordinates);
  const areaPath = smoothPath + 
    ` L ${coordinates[coordinates.length - 1].x}% ${chartHeight - padding}` +
    ` L ${coordinates[0].x}% ${chartHeight - padding} Z`;

  return (
    <div className="relative bg-gradient-to-b from-orange-50/20 to-transparent rounded-lg pt-2" style={{ height: `${chartHeight + 30}px` }}>
      <svg
        width="100%"
        height={chartHeight}
        className="overflow-visible"
      >
        <defs>
          <linearGradient id="participationGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F97316" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#F97316" stopOpacity="0.03" />
          </linearGradient>
          <linearGradient id="participationLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#FB923C" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={i}
            x1={`${padding}%`}
            y1={padding + (i * (chartHeight - padding * 2)) / 4}
            x2={`${100 - padding}%`}
            y2={padding + (i * (chartHeight - padding * 2)) / 4}
            stroke="#F3F4F6"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        ))}
        <path
          d={areaPath}
          fill="url(#participationGradient)"
          className="transition-all duration-500"
        />
        <path
          d={smoothPath}
          fill="none"
          stroke="url(#participationLineGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-all duration-500"
        />
        {coordinates.map((coord, i) => (
          <circle
            key={i}
            cx={`${coord.x}%`}
            cy={coord.y}
            r="4"
            fill="#F97316"
            stroke="white"
            strokeWidth="2"
            className="transition-all duration-300"
          />
        ))}
        {data.map((d, i) => (
          <text
            key={i}
            x={`${coordinates[i].x}%`}
            y={chartHeight + 12}
            textAnchor="middle"
            className="text-xs font-medium fill-gray-600"
          >
            {d.day}
          </text>
        ))}
      </svg>
      <div className="absolute left-0 top-2 flex flex-col justify-between" style={{ height: `${chartHeight - padding * 2}px`, width: '30px' }}>
        {[0, 1, 2, 3, 4].reverse().map((i) => (
          <span key={i} className="text-xs text-gray-500">
            {Math.round((i * maxParticipants) / 4)}
          </span>
        ))}
      </div>
    </div>
  );
};

// Attendee Demographics Component
const AttendeeDemographics = ({ demographics }) => {
  return (
    <div className="space-y-4">
      {/* Age Groups */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <CalendarIcon className="w-4 h-4 text-blue-600" />
          <h4 className="text-xs font-semibold text-gray-900">Age Groups</h4>
        </div>
        <div className="space-y-1.5">
          {demographics.ageGroups.map((group, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-medium text-gray-700">{group.group}</span>
                <span className="text-xs font-semibold text-gray-900">
                  {group.count} ({group.percentage}%)
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                  style={{ width: `${group.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gender Distribution */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <Users className="w-4 h-4 text-purple-600" />
          <h4 className="text-xs font-semibold text-gray-900">Gender</h4>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {demographics.gender.map((item, idx) => (
            <div key={idx} className="p-2 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 text-center">
              <p className="text-xs font-medium text-gray-600 mb-0.5">{item.type}</p>
              <p className="text-lg font-bold text-purple-700">{item.count}</p>
              <p className="text-xs text-gray-600">{item.percentage}%</p>
            </div>
          ))}
        </div>
      </div>

      {/* Location Distribution */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <MapPin className="w-4 h-4 text-green-600" />
          <h4 className="text-xs font-semibold text-gray-900">Location</h4>
        </div>
        <div className="space-y-1.5">
          {demographics.location.map((loc, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-medium text-gray-700">{loc.city}</span>
                <span className="text-xs font-semibold text-gray-900">
                  {loc.count} ({loc.percentage}%)
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                  style={{ width: `${loc.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Companies Overview Component
const CompaniesOverview = ({ overview }) => {
  return (
    <div className="space-y-6">
      {/* Services Interest */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <Briefcase className="w-4 h-4 text-indigo-600" />
          <h4 className="text-sm font-semibold text-gray-900">Services Interest</h4>
        </div>
        <div className="space-y-2">
          {overview.servicesInterest.map((item, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">
                    {item.code}
                  </span>
                  <span className="text-xs font-medium text-gray-700">{item.label}</span>
                </div>
                <span className="text-xs font-semibold text-gray-900">
                  {item.count} ({item.percentage}%)
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all duration-500"
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Nature of Company */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <Building2 className="w-4 h-4 text-purple-600" />
          <h4 className="text-sm font-semibold text-gray-900">Nature of Company</h4>
        </div>
        <div className="space-y-2">
          {overview.natureOfCompany.map((item, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                    {item.code}
                  </span>
                  <span className="text-xs font-medium text-gray-700">{item.label}</span>
                </div>
                <span className="text-xs font-semibold text-gray-900">
                  {item.count} ({item.percentage}%)
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-500"
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TradeMissionAnalytics;

